import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FloorPlan } from '../components/FloorPlan';
import { BoothModal } from '../components/BoothModal';
import { Booth, CreateOrderResponse } from '../lib/api';
import {
  CheckCircle2,
  Building2,
  Layers,
  ArrowRight,
} from 'lucide-react';

export function FloorPlanPage() {
  const [selectedBooth, setSelectedBooth] = useState<Booth | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState<CreateOrderResponse | null>(null);

  const handleSelectBooth = (booth: Booth) => {
    setSelectedBooth(booth);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* Quick Switcher Banner: Already Booked Stall */}
      <div className="bg-gradient-to-r from-emerald-50/70 via-white to-[#FAF5EA]/50 border border-emerald-200 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-[#0A3D27] border border-emerald-200 flex items-center justify-center shrink-0 shadow-2xs">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <span>Already reserved an exhibition stall?</span>
              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-[#0A3D27] border border-emerald-200">
                Exhibitor Portal
              </span>
            </div>
            <p className="text-xs text-slate-600">
              Submit your fascia board name, setup directory profile, and claim your 2 complimentary delegate badges.
            </p>
          </div>
        </div>

        <Link
          to="/exhibitor-portal"
          className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-xs transition-all shadow-md shadow-emerald-950/20 group cursor-pointer"
        >
          <span>Already Booked? Go to Exhibitor Portal</span>
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* Page Header */}
      <div className="border-b border-slate-200 pb-6 space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[#0A3D27] text-xs font-bold">
          <Layers className="w-3.5 h-3.5" />
          <span>Pavilion 3 Exhibition Hall • Jio World Convention Centre</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-[#0F172A] tracking-tight">
          Interactive Floor Plan & Stall Booking Engine
        </h1>
        <p className="text-sm text-slate-600 max-w-3xl leading-relaxed">
          Select any available exhibition stall to inspect live pricing, dimensions, and lock for 15 minutes.
          All domestic bookings include 18% GST invoicing with immediate Razorpay payment or purchase order generation.
        </p>
      </div>

      {/* Order Confirmation Banner */}
      {confirmedOrder && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-5 flex items-center justify-between animate-in fade-in duration-200 shadow-xs">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-7 h-7 text-[#0A3D27] shrink-0" />
            <div>
              <p className="text-sm font-bold text-slate-900">Order {confirmedOrder.orderId} Confirmed!</p>
              <p className="text-xs text-slate-700">
                Stall {confirmedOrder.boothId} has been successfully reserved for {confirmedOrder.companyName}.
                A booking receipt and exhibitor manual have been dispatched to your email.
              </p>
            </div>
          </div>
          <button
            onClick={() => setConfirmedOrder(null)}
            className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg bg-white border border-slate-200 cursor-pointer shadow-2xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Interactive 2D Blueprint Canvas */}
      <FloorPlan onSelectBooth={handleSelectBooth} selectedBooth={selectedBooth} />

      {/* Stall Specifications & Inclusions Grid */}
      <section className="space-y-6 pt-6">
        <div>
          <span className="text-xs font-bold text-[#0A3D27] uppercase tracking-wider block">
            Exhibitor Entitlements
          </span>
          <h2 className="text-2xl font-black text-[#0F172A] tracking-tight mt-1">
            Stall Categories & Standard Deliverables
          </h2>
          <p className="text-xs text-slate-600 mt-1">
            All prefabricated stalls are constructed with high-grade German Octanorm modular systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 text-xs">
          {/* 3x3m Shell Scheme */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-4 shadow-xs hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className="w-3.5 h-3.5 rounded bg-sky-100 border border-sky-500" />
              <span className="font-extrabold text-sky-800">₹85,000 + GST</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">3 x 3 = 9 sqm Shell Scheme</h3>
              <p className="text-slate-600 mt-1">
                Standard B2B presence tailored for dynamic product display and client meetings.
              </p>
            </div>
            <ul className="space-y-1.5 text-slate-700 border-t border-slate-100 pt-3">
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Octanorm walls (2.5m height)</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Fascia name board with company lettering</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 3 Spotlights & 1 x 5A power socket</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 1 Information counter & 2 chairs</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 2 Exhibitor badges & catalog listing</li>
            </ul>
          </div>

          {/* 4x3m Premium */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-4 shadow-xs hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className="w-3.5 h-3.5 rounded bg-amber-100 border border-amber-500" />
              <span className="font-extrabold text-amber-800">₹1,15,000 + GST</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">4 x 3 = 12 sqm Premium Corner</h3>
              <p className="text-slate-600 mt-1">
                Corner or aisle-facing location offering enhanced footfall visibility.
              </p>
            </div>
            <ul className="space-y-1.5 text-slate-700 border-t border-slate-100 pt-3">
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Two-side open corner allocation</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Dual fascia boards with corporate logos</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 4 Spotlights & 1 x 15A power connection</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 1 Reception counter, 1 round table & 3 chairs</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 3 Exhibitor badges & priority networking</li>
            </ul>
          </div>

          {/* 6x3m Grand */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-4 shadow-xs hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className="w-3.5 h-3.5 rounded bg-rose-100 border border-rose-500" />
              <span className="font-extrabold text-rose-800">₹1,75,000 + GST</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">6 x 3 = 18 sqm Grand Booth</h3>
              <p className="text-slate-600 mt-1">
                Expansive frontage for active ingredient manufacturers and enterprise suppliers.
              </p>
            </div>
            <ul className="space-y-1.5 text-slate-700 border-t border-slate-100 pt-3">
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Double width frontage (6 meters)</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Custom graphics fascia & backlit signage</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 6 Spotlights & dual power connections</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 2 Discussion tables & 6 chairs</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 4 Exhibitor passes & VIP networking passes</li>
            </ul>
          </div>

          {/* 6x6m Grand Island */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-4 shadow-xs hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className="w-3.5 h-3.5 rounded bg-fuchsia-100 border border-fuchsia-500" />
              <span className="font-extrabold text-fuchsia-800">₹3,60,000 + GST</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">6 x 6 = 36 sqm Grand Island</h3>
              <p className="text-slate-600 mt-1">
                4-side open standalone island stall positioned on the main arrival avenue.
              </p>
            </div>
            <ul className="space-y-1.5 text-slate-700 border-t border-slate-100 pt-3">
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 360-degree all-aisle open visibility</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Bare space or custom shell fabrication</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> High 3-phase industrial power supply</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 6 Exhibitor passes & 2 VIP Lounge invites</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Full-page color ad in official show guide</li>
            </ul>
          </div>

          {/* 6 sqm Trading Table */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-4 shadow-xs hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className="w-3.5 h-3.5 rounded bg-emerald-100 border border-[#0A3D27]" />
              <span className="font-extrabold text-[#0A3D27]">₹55,000 + GST</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">6 sqm Trading Table (TT1–12)</h3>
              <p className="text-slate-600 mt-1">
                Cost-effective B2B desk setup for traders, consultants, and tech startups.
              </p>
            </div>
            <ul className="space-y-1.5 text-slate-700 border-t border-slate-100 pt-3">
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Dedicated branded consultation table</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 2 Executive conference chairs</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 1 Laptop power connection</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 1 Tabletop company tent card</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 1 Trade delegate badge included</li>
            </ul>
          </div>

          {/* 9 sqm Meeting Room */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-6 space-y-4 shadow-xs hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className="w-3.5 h-3.5 rounded bg-purple-100 border border-purple-500" />
              <span className="font-extrabold text-purple-800">₹95,000 + GST</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">9 sqm Meeting Suite (MR1–7)</h3>
              <p className="text-slate-600 mt-1">
                Private enclosed suite on the quiet north wall for confidential discussions.
              </p>
            </div>
            <ul className="space-y-1.5 text-slate-700 border-t border-slate-100 pt-3">
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Fully enclosed acoustic drywall partition</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Lockable access door with company placard</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Air-conditioned comfort & internal lighting</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> 4-person executive conference table</li>
              <li className="flex items-center gap-2"><span className="text-[#0A3D27] font-bold">✓</span> Complimentary tea & coffee hospitality service</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Reservation Modal */}
      <BoothModal
        booth={selectedBooth}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBooth(null);
        }}
        onLocked={(updated) => {
          setSelectedBooth(updated);
        }}
        onOrderSuccess={(order) => {
          setConfirmedOrder(order);
        }}
      />
    </div>
  );
}

export default FloorPlanPage;
